/**
 * Shared Image Fetching Service
 * 
 * Provides centralized image generation and fetching functionality
 * for Grok AI image generation and Unsplash stock photos.
 * 
 * Used by:
 * - image_add tool
 * - canvas_write tool
 * - slide_write tool
 * - note_write tool
 * - entity_write tool
 */

import fetchService from './fetchService';
import useUsageLimitsStore, { type UsageLimitInfo } from '../stores/usageLimitsStore';

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

export interface ImageResult {
  url: string;
  metadata: ImageMetadata;
}

export interface ImageMetadata {
  width?: number;
  height?: number;
  source: 'grok' | 'unsplash' | 'url';
  model?: string;
  prompt?: string;
  photographer?: string;
  photographerUrl?: string;
  description?: string;
  fileName?: string;
}

export interface GrokImageOptions {
  model?: string;
  variants?: number;
  seed?: number;
  width?: number;
  height?: number;
}

export interface UnsplashImageOptions {
  orientation?: 'landscape' | 'portrait' | 'squarish';
  color?: string;
}

// ═══════════════════════════════════════════════════════════════════════════
// API BASE URL UTILITY
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Get API base URL based on environment
 */
export function getApiBaseUrl(): string {
  // Handle server-side rendering (SSR) context
  if (typeof window === 'undefined') {
    // Server-side fallback - use environment variable or default
    return process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
  }
  
  const hostname = window.location.hostname;
  
  if (hostname === 'app.riversystem.ai' || hostname === 'riversystem.ai' || hostname.endsWith('.riversystem.ai')) {
    return 'https://app.riversystem.ai/api';
  }
  
  if (hostname === 'rivereditor.com') {
    return 'https://api.rivereditor.com/api';
  }
  
  return 'http://localhost:5001/api';
}

// ═══════════════════════════════════════════════════════════════════════════
// GROK IMAGE GENERATION
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Generate image using Grok/xAI
 * 
 * @param prompt - Text description of the image to generate
 * @param options - Optional generation parameters
 * @returns Image URL and metadata
 */
export async function generateGrokImage(
  prompt: string,
  options: GrokImageOptions = {}
): Promise<ImageResult> {
  // Validate prompt
  if (!prompt || !prompt.trim()) {
    throw new Error('Prompt is required and cannot be empty');
  }
  
  const model = options.model || 'grok-imagine-image';
  const variants = options.variants || 1;
  
  console.log('[ImageService] Generating with Grok/xAI:', {
    model,
    prompt: prompt.length > 50 ? prompt.substring(0, 50) + '...' : prompt
  });
  
  // Determine API base URL
  const apiBaseUrl = getApiBaseUrl();
  
  // Call Grok endpoint with error handling
  let result;
  try {
    result = await fetchService(`${apiBaseUrl}/grok/image/generate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        prompt,
        model,
        variants,
        seed: options.seed
      })
    });
  } catch (error: any) {
    const detail = error.data?.detail ?? error.data;
    if (detail?.upgrade_required) {
      const limitType = (detail.limit_type ?? 'imageGenerations') as UsageLimitInfo['limitType'];
      useUsageLimitsStore.getState().showUpgradeModal({
        limitType,
        currentUsage: {
          used: detail.limits?.[limitType]?.used ?? 0,
          max:  detail.limits?.[limitType]?.max  ?? 0,
          unit: 'images',
        },
        planName: detail.plan_type ?? 'free',
        message:  detail.message,
      });
      throw new Error('Image generation requires a plan upgrade.');
    }
    throw new Error(`Failed to connect to Grok API: ${error.message}`);
  }
  
  // Check for API errors
  if (result.error) {
    throw new Error(`Grok API error: ${result.error.message || result.error}`);
  }
  
  if (!result.success || !result.images || result.images.length === 0) {
    throw new Error('Grok image generation returned no images');
  }
  
  console.log('[ImageService] Grok generation complete, uploading to storage...');
  
  // Convert base64 to permanent URL
  const base64Image = result.images[0];
  const permanentUrl = await uploadBase64ToStorage(base64Image, 'grok-generated.png');
  
  return {
    url: permanentUrl,
    metadata: {
      source: 'grok',
      model: result.model,
      prompt
    }
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// UNSPLASH STOCK PHOTOS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Search and download Unsplash photo
 * 
 * @param query - Search query for Unsplash
 * @param options - Optional search filters
 * @returns Image URL and metadata
 */
export async function searchAndDownloadUnsplash(
  query: string,
  options: UnsplashImageOptions = {}
): Promise<ImageResult> {
  // Validate query
  if (!query || !query.trim()) {
    throw new Error('Query is required and cannot be empty');
  }
  
  console.log('[ImageService] Searching Unsplash:', query);
  
  // Determine API base URL
  const apiBaseUrl = getApiBaseUrl();
  
  // Step 1: Search
  const searchParams = new URLSearchParams({
    query,
    per_page: '1',
    ...(options.orientation && { orientation: options.orientation }),
    ...(options.color && { color: options.color })
  });
  
  let searchData;
  try {
    searchData = await fetchService(`${apiBaseUrl}/unsplash/search?${searchParams}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' }
    });
  } catch (error: any) {
    throw new Error(`Failed to connect to Unsplash API: ${error.message}`);
  }
  
  if (!searchData.results || searchData.results.length === 0) {
    throw new Error(`No Unsplash photos found for "${query}". Try a different search term.`);
  }
  
  const photo = searchData.results[0];
  console.log('[ImageService] Found Unsplash photo:', {
    id: photo.id,
    photographer: photo.user.name
  });
  
  // Step 2: Download (this automatically uploads to S3!)
  let downloadData;
  try {
    downloadData = await fetchService(`${apiBaseUrl}/unsplash/download`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ photo_id: photo.id })
    });
  } catch (error: any) {
    throw new Error(`Failed to download Unsplash photo: ${error.message}`);
  }
  
  console.log('[ImageService] Unsplash photo downloaded and uploaded to storage');
  
  return {
    url: downloadData.image_entity.url,
    metadata: {
      width: downloadData.image_entity.width,
      height: downloadData.image_entity.height,
      source: 'unsplash',
      photographer: downloadData.image_entity.unsplash.photographer.name,
      photographerUrl: downloadData.image_entity.unsplash.photographer.profile,
      description: downloadData.image_entity.unsplash.description
    }
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// STORAGE UTILITIES
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Upload base64 image to storage and get permanent URL
 * 
 * @param base64Data - Base64-encoded image data
 * @param filenamePrefix - Prefix for the generated filename
 * @returns Permanent image URL
 */
export async function uploadBase64ToStorage(
  base64Data: string, 
  filenamePrefix: string = 'generated'
): Promise<string> {
  console.log('[ImageService] Converting base64 to permanent URL...');
  
  // Extract image type and data
  const base64Match = base64Data.match(/^data:image\/([a-zA-Z]*);base64,(.+)$/);
  if (!base64Match) {
    throw new Error('Invalid base64 image data format');
  }
  
  const [, imageType, actualBase64] = base64Match;
  // Use the prefix to create a descriptive filename
  const generatedFilename = `${filenamePrefix}-${Date.now()}.${imageType}`;
  
  // Convert to blob
  const byteCharacters = atob(actualBase64);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], { type: `image/${imageType}` });
  
  // Upload to S3 via /api/files/upload
  const formData = new FormData();
  formData.append('file', blob, generatedFilename);
  
  const apiBaseUrl = getApiBaseUrl();
  const authToken = localStorage.getItem('authToken');
  
  const response = await fetch(`${apiBaseUrl}/files/upload`, {
    method: 'POST',
    headers: {
      ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {})
    },
    body: formData,
    credentials: 'include'  // Send cookies as fallback auth
  });
  
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to upload image to storage: ${response.status} - ${errorText}`);
  }
  
  const uploadResult = await response.json();
  
  if (!uploadResult.url) {
    throw new Error('Upload succeeded but no URL returned');
  }
  
  console.log('[ImageService] ✅ Image uploaded to storage:', uploadResult.url);
  
  return uploadResult.url;
}
